import React, { useEffect, useState } from "react";
import Header from "../WelcomeHeader";
import { ReactComponent as IncompleteImg } from "../../../Assets/Images/incompleteImg.svg";
import PrimaryButton from "../PrimaryButton";
import { useDispatch, useSelector } from "react-redux";
import { createConnectedAccount, getOnboardingStatus } from "../../../services/fundraiserService";
import { useNavigate } from "react-router-dom";
import { getFcmToken } from "../../../utils/connectStore";
import { clearAuth } from "../../../redux/slices/authSlice";
import Loader from "../Loader";
import axios from "../../../utils/axios";

const CompleteSetup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const [isChecking, setIsChecking] = useState(true);

  // If user returns from Stripe (or just opened this page), re-check onboarding
  // and redirect appropriately - clear auth and send to fundraiser login if Stripe is complete
  useEffect(() => {
    let timeoutId;
    const refresh = async () => {
      const clearFundraiserSessionState = () => {
        sessionStorage.removeItem("showAccountPendingModal");
        sessionStorage.removeItem("fundraiser_stripe_complete");
        sessionStorage.removeItem("fundraiser_block_status");
        sessionStorage.removeItem("fundraiser_verified");
        sessionStorage.removeItem("fundraiser_waitlist_token");
      };

      const redirectBlockedFundraiser = (profileStatus) => {
        const statusLower = String(profileStatus || "").toLowerCase();
        let message = "Your account is pending admin approval. Please wait for approval.";
        let showPendingModal = true;
        let blockStatus = "pending";

        if (statusLower === "disabled") {
          message = "Your account is disabled. Please contact support.";
          showPendingModal = false;
          blockStatus = "disabled";
        } else if (statusLower === "deleted") {
          message = "Your account have been deleted. Please Contact Support";
          showPendingModal = false;
          blockStatus = "deleted";
        } else if (statusLower === "rejected") {
          message = "Your account has been rejected. Please contact support.";
          blockStatus = "rejected";
        } else if (statusLower === "declined") {
          message = "Your account has been declined. Please contact support.";
          blockStatus = "declined";
        }

        dispatch(clearAuth());
        clearFundraiserSessionState();
        if (token) {
          sessionStorage.setItem("fundraiser_waitlist_token", token);
        }
        sessionStorage.setItem("fundraiser_block_status", blockStatus);
        if (showPendingModal) {
          sessionStorage.setItem("showAccountPendingModal", "true");
        }
        if (blockStatus === "pending") {
          // navigate("/fundraiser/account-login", {
          //   replace: true,
          //   state: { message, showAccountPendingModal: showPendingModal },
          // });
          navigate("/fundraiser/coming-soon", {
            replace: true,
            state: { message, showAccountPendingModal: showPendingModal },
          });
        } else {
          navigate("/fundraiser/account-login", {
            replace: true,
            state: { message, showAccountPendingModal: showPendingModal },
          });
        }
      };

      // First check if user has a token - if not, they shouldn't be here
      if (!token) {
        setIsChecking(false);
        navigate("/fundraiser/account-login", { replace: true });
        return;
      }

      try {
        const checkResponse = await axios.get("fundraiser/profile", {
          headers: {
            Authorization: `Bearer ${token}`,
            "api-key": process.env.REACT_APP_API_KEY,
            "Cache-Control": "no-cache",
          },
          params: { _: Date.now() },
        });

        const fundraiserProfile = checkResponse?.data?.data;
        const profileStatusRaw = fundraiserProfile?.status;
        const profileStatus = typeof profileStatusRaw === "string"
          ? profileStatusRaw.toLowerCase()
          : profileStatusRaw;
        const isApproved = profileStatus === "approved" || profileStatus === "live";

        if (!isApproved) {
          redirectBlockedFundraiser(profileStatus);
          return;
        }

        const statusPromise = await dispatch(getOnboardingStatus()).unwrap();
        const detailsSubmitted = statusPromise?.data?.detailsSubmitted === true;
        const chargesEnabled = statusPromise?.data?.chargesEnabled === true;
        
        // Check if Stripe onboarding is complete
        if (detailsSubmitted && chargesEnabled) {
          // Set the flag to indicate Stripe completion
          sessionStorage.setItem('fundraiser_stripe_complete', 'true');
          
          navigate("/fundraiser/dashboard", { replace: true });
          return;
        }
      } catch (_) {
        dispatch(clearAuth());
        clearFundraiserSessionState();
        navigate("/fundraiser/account-login", {
          replace: true,
          state: {
            message: "Failed to verify account status. Please try again.",
          },
        });
        return;
      }
      setIsChecking(false); // Only show content if Stripe not complete
    };
    refresh();
    
    // Cleanup timeout on unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async () => {
    const linkPromise = await dispatch(createConnectedAccount()).unwrap();
    window.location.href = linkPromise?.data;
  };

  // Show loader while checking to prevent dashboard flash
  if (isChecking) {
    return <Loader />;
  }

  return (
    <div className="w-full flex flex-col justify-center items-center">
      <Header />
      <div className="sm:w-[80%] md:w-[70%] lg:w-[60%] py-20">
        <p className="font-poppins font-[500] text-[28px] text-center text-[#303030]">
          Complete Your Account Setup
        </p>
        <p className="font-poppins font-[500] text-[18px] text-start text-[#303030] mt-10">
          Account Setup
        </p>
        <p className="font-poppins font-[400] text-[14px] text-start text-[#767676] mt-2">
          It appears that your account setup is not yet complete. We require
          additional information about your business, along with the necessary
          legal documents. Kindly finalize your account details to gain access
          to the Swoodle fundraising portal and begin utilizing its features.
        </p>
        <div className="mt-20 flex justify-center items-center">
          <IncompleteImg />
        </div>
        <div className="w-full text-end mt-20">
          <PrimaryButton
            className={
              "w-[30%] font-montserrat text-[14px] text-center font-[500] h-[45px] rounded-[5px]"
            }
            label="Complete Account Setup"
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default CompleteSetup;
